<template>
  <teleport to="body">
    <div class="admin-portal uam-overlay" @click.self="close" @keydown.esc="close">
      <div class="uam-modal" role="dialog" aria-modal="true">
        <!-- Header -->
        <div class="uam-head">
          <div class="uam-head__titles">
            <h3 class="uam-title">
              <i class="fa-solid fa-user-clock"></i>
              {{ user ? (user.fullName || user.userName || "User Activity") : "User Activity" }}
            </h3>
            <p class="uam-sub" v-if="user">
              {{ user.email }}
              <span class="admin-badge" :class="user.role === 'user' ? 'admin-badge--neutral' : 'admin-badge--purple'">{{ user.role || 'user' }}</span>
              <span v-if="user.subscriptionPlan" class="admin-badge admin-badge--info">{{ user.subscriptionPlan }}</span>
              <span class="admin-badge" :class="user.activated ? 'admin-badge--success' : 'admin-badge--warning'">
                {{ user.activated ? "activated" : "not activated" }}
              </span>
              <span class="admin-badge" :class="authTypeBadge" :title="`Signs in with ${authTypeText}`">
                <i :class="authTypeIcon"></i> {{ authTypeText }}
              </span>
            </p>
          </div>
          <button type="button" class="uam-close" @click="close" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="uam-body">
          <div v-if="headerLoading" class="admin-loading"><div class="admin-spinner"></div><span>Loading…</span></div>

          <div v-else-if="loadError" class="admin-badge admin-badge--danger admin-section">{{ loadError }}</div>

          <template v-else>
            <!-- Tabs -->
            <div class="admin-tabs admin-section">
              <button
                v-for="t in TABS"
                :key="t.key"
                class="admin-tab"
                :class="{ 'admin-tab--active': tab === t.key }"
                @click="selectTab(t.key)"
              >
                <i class="fa-solid" :class="t.icon"></i> {{ t.label }}
              </button>
            </div>

            <!-- Tab body -->
            <div class="admin-card admin-card--flush admin-section">
              <div v-if="tabLoading" class="admin-loading"><div class="admin-spinner"></div><span>Loading…</span></div>

              <!-- Overview -->
              <div v-else-if="tab === 'overview'" class="uam-overview">
                <div class="uam-kv">
                  <h3 class="admin-card__title">Account</h3>
                  <table class="admin-table admin-table--rows">
                    <tbody>
                      <tr v-for="[k, v] in accountRows" :key="k">
                        <td class="uam-key">{{ k }}</td>
                        <td>{{ v }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="uam-kv" v-if="detailRows.length">
                  <h3 class="admin-card__title">Profile Details</h3>
                  <table class="admin-table admin-table--rows">
                    <tbody>
                      <tr v-for="[k, v] in detailRows" :key="k">
                        <td class="uam-key">{{ k }}</td>
                        <td>{{ v }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Logins -->
              <div v-else-if="tab === 'logins'" class="admin-table-wrap">
                <table class="admin-table admin-table--rows">
                  <thead>
                    <tr>
                      <th>#</th><th>Login Time</th><th>Last Active</th><th>Status</th>
                      <th :title="IP_HINT">IP Address</th>
                      <th :title="CLICKS_HINT">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in rows" :key="i">
                      <td>{{ rowNum(i) }}</td>
                      <td>{{ fmtDateTime(row.loginTime) }}</td>
                      <td>{{ fmtDateTime(row.lastActiveTime) }}</td>
                      <td><span class="admin-badge admin-badge--neutral">{{ row.status || "—" }}</span></td>
                      <td class="uam-nowrap" :title="ipTitle(row)">
                        {{ row.ip || "—" }}
                        <span v-if="extraIpCount(row)" class="uam-ip-more">+{{ extraIpCount(row) }}</span>
                      </td>
                      <td>{{ row.clicks || 0 }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Clicks -->
              <div v-else-if="tab === 'clicks'" class="admin-table-wrap">
                <table class="admin-table admin-table--rows">
                  <thead>
                    <tr>
                      <th>#</th><th>Time</th>
                      <th :title="LOGIN_TIME_HINT">Login Time</th>
                      <th>Click</th><th>Detail</th><th>Page</th><th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in rows" :key="i">
                      <td>{{ rowNum(i) }}</td>
                      <td class="uam-nowrap">{{ fmtDateTime(row.createdAt) }}</td>
                      <td class="uam-nowrap uam-muted">{{ row.loginTime ? fmtDateTime(row.loginTime) : "—" }}</td>
                      <td class="uam-nowrap">
                        <span class="admin-badge admin-cat" :class="catBadgeClass(row.category)">{{ catLabel(row.category) }}</span>
                        {{ actionLabel(row.action) }}
                      </td>
                      <td>
                        <a
                          v-if="row.targetUrl"
                          :href="row.targetUrl"
                          target="_blank"
                          rel="noopener"
                        >{{ clickDetail(row) }}</a>
                        <template v-else>{{ clickDetail(row) }}</template>
                      </td>
                      <td class="uam-nowrap">{{ row.path || "—" }}</td>
                      <td class="uam-nowrap">{{ row.ip || "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Page Views -->
              <div v-else-if="tab === 'views'" class="admin-table-wrap">
                <table class="admin-table admin-table--rows">
                  <thead>
                    <tr><th>#</th><th>Time</th><th>Page / Listing</th><th>Device</th><th>Location</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in rows" :key="i">
                      <td>{{ rowNum(i) }}</td>
                      <td class="uam-nowrap">{{ fmtDateTime(row.timestamp) }}</td>
                      <td>
                        <a
                          v-if="postUrl(row)"
                          :href="postUrl(row)"
                          target="_blank"
                          rel="noopener"
                          :title="cleanTitle(row.serviceTitle) ? `${row.servicesName} · ${cleanTitle(row.serviceTitle)}` : row.servicesName"
                        >{{ row.servicesName || cleanTitle(row.serviceTitle) }}</a>
                        <template v-else>{{ row.servicesName || cleanTitle(row.serviceTitle) || row.pageName || row.Description || "—" }}</template>
                      </td>
                      <td>{{ [row.browser, row.os, row.device].filter(Boolean).join(" · ") || "—" }}</td>
                      <td>{{ [row.location && row.location.city, row.location && row.location.regionName].filter(Boolean).join(", ") || "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Posts -->
              <div v-else-if="tab === 'posts'" class="admin-table-wrap">
                <table class="admin-table admin-table--rows">
                  <thead>
                    <tr><th>#</th><th>Listing</th><th>Category</th><th>Status</th><th>Created</th><th>Updated</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in rows" :key="row.servicesID || i">
                      <td>{{ rowNum(i) }}</td>
                      <td>
                        <a
                          v-if="postUrl(row)"
                          :href="postUrl(row)"
                          target="_blank"
                          rel="noopener"
                          :title="cleanTitle(row.serviceTitle) ? `${row.servicesName} · ${cleanTitle(row.serviceTitle)}` : row.servicesName"
                        >{{ row.servicesName || cleanTitle(row.serviceTitle) }}</a>
                        <template v-else>{{ row.servicesName || cleanTitle(row.serviceTitle) || "—" }}</template>
                      </td>
                      <td>{{ [row.serviceType1, row.serviceType2].filter(Boolean).join(" / ") }}</td>
                      <td>
                        <span class="admin-badge" :class="isActive(row) ? 'admin-badge--success' : 'admin-badge--danger'">
                          {{ isActive(row) ? "active" : "inactive" }}
                        </span>
                      </td>
                      <td class="uam-nowrap">{{ fmtDate(row.createdTime) }}</td>
                      <td class="uam-nowrap">{{ fmtDate(row.updatedTime) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Messages -->
              <div v-else-if="tab === 'messages'" class="admin-table-wrap">
                <table class="admin-table admin-table--rows">
                  <thead>
                    <tr><th>#</th><th>Time</th><th></th><th>With</th><th>Message</th><th>Read</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in rows" :key="i">
                      <td>{{ rowNum(i) }}</td>
                      <td class="uam-nowrap">{{ fmtDateTime(row.createdTime) }}</td>
                      <td>
                        <span class="admin-badge" :class="row.fromUserId === uid ? 'admin-badge--info' : 'admin-badge--neutral'">
                          {{ row.fromUserId === uid ? "sent" : "received" }}
                        </span>
                      </td>
                      <td>{{ row.counterpartName || row.counterpartId || "—" }}</td>
                      <td class="uam-msg">{{ row.message }}</td>
                      <td>{{ row.isRead ? "✓" : "—" }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="!tabLoading && tab !== 'overview' && !rows.length" class="admin-empty">
                <div class="admin-empty__title">Nothing here</div>
                <p>No {{ tabLabel.toLowerCase() }} recorded for this user.</p>
              </div>

              <!-- Pagination (list tabs only) -->
              <div class="admin-pagination" v-if="!tabLoading && tab !== 'overview' && totalPages > 1">
                <span class="admin-pagination__info">{{ totalCount }} rows · page {{ page }} of {{ totalPages }}</span>
                <button class="admin-btn admin-btn--sm admin-btn--outline" :disabled="page <= 1" @click="goPage(page - 1)">
                  <i class="fa-solid fa-chevron-left"></i> Prev
                </button>
                <button class="admin-btn admin-btn--sm admin-btn--outline" :disabled="page >= totalPages" @click="goPage(page + 1)">
                  Next <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import axiosInst from "@/utils/axiosinstance";
import { postUrl } from "@/utils/postUrl";
import { authTypeLabel, authTypeBadgeClass, authTypeIconClass } from "@/utils/authType";
// Shared with Interaction Analytics so a new tracked action is labelled once.
import { actionLabel, categoryLabel, categoryBadgeClass, clickDetail } from "@/utils/interactionLabels";

// Neither `userlog` nor `user` stores an IP, so the address on a login row and
// in the Account table is derived server-side from the tracked activity of the
// same day. A user with no tracked events that day shows "—".
const IP_HINT = "IP this user was seen on that day (derived from tracked activity — userlog stores no IP)";
const CLICKS_HINT = "Tracked clicks this user made that day. Page views are not counted.";
// Clicks are stamped with the session they belong to: the newest login at or
// before the click. Blank for clicks that predate the user's first login row.
const LOGIN_TIME_HINT = "Start of the login session this click belongs to";

// Internal/bookkeeping keys hidden from the Overview key-value tables.
const HIDDEN_KEYS = new Set([
  "_id", "__v", "password", "tenantID", "orgID", "profileID", "slug",
  "createdBy", "updatedBy", "passwordMigrated", "userID", "detailsID",
  // Rendered as a formatted "Authentication Type" row instead of a raw dump.
  "authType", "authTypeLabel",
]);

// Bookkeeping timestamps on the userdetails record. They say nothing about the
// profile itself, so the Profile Details table drops them and leads with the
// user's ID instead — that is what support actually needs off this popup.
const DETAIL_HIDDEN_KEYS = new Set(["createdTime", "updatedTime"]);

export default {
  name: "UserActivityModal",
  props: {
    // The user whose activity to show. userId is required for the activity
    // tabs; email / userName are sent as fallbacks for the detail lookup so
    // rows that only carry an email still resolve the header.
    userId: { type: [String, Number], default: "" },
    email: { type: String, default: "" },
    userName: { type: String, default: "" },
  },
  emits: ["close"],
  data() {
    return {
      TABS: [
        { key: "overview", label: "Overview", icon: "fa-id-card" },
        { key: "logins", label: "Logins", icon: "fa-right-to-bracket" },
        { key: "clicks", label: "Clicks", icon: "fa-hand-pointer" },
        { key: "views", label: "Page Views", icon: "fa-eye" },
        { key: "posts", label: "Posts", icon: "fa-rectangle-list" },
        { key: "messages", label: "Messages", icon: "fa-comments" },
      ],
      user: null,
      details: null,
      summary: null,
      tab: "overview",
      rows: [],
      page: 1,
      totalPages: 0,
      totalCount: 0,
      headerLoading: false,
      tabLoading: false,
      loadError: "",
      IP_HINT,
      CLICKS_HINT,
      LOGIN_TIME_HINT,
    };
  },
  computed: {
    uid() {
      // Prefer the resolved user record's ID; fall back to the passed prop.
      return (this.user && this.user.userID) || this.userId || "";
    },
    tabLabel() {
      const t = this.TABS.find((x) => x.key === this.tab);
      return t ? t.label : "";
    },
    authTypeText() {
      return authTypeLabel(this.user && this.user.authType);
    },
    authTypeBadge() {
      return authTypeBadgeClass(this.user && this.user.authType);
    },
    authTypeIcon() {
      return authTypeIconClass(this.user && this.user.authType);
    },
    accountRows() {
      // Authentication type leads the Account table — it is the first thing
      // support asks when a user says they cannot log in.
      if (!this.user) return [];
      // Clicks / last IP come from the activity summary, not the user record —
      // see the Clicks tab for the individual events behind the total.
      const activity = this.summary
        ? [
            ["Total Clicks", String(this.summary.clickCount ?? 0)],
            ["Last Known IP", this.summary.lastIp || "—"],
            ["IP Addresses Seen", String(this.summary.ipCount ?? 0)],
          ]
        : [];
      return [["Authentication Type", this.authTypeText], ...activity, ...this.toRows(this.user)];
    },
    detailRows() {
      const rows = this.toRows(this.details, DETAIL_HIDDEN_KEYS);
      // Keep the section hidden when there is no profile record at all — a
      // lone User ID row would just be noise.
      if (!rows.length) return [];
      return [["User ID", String((this.user && this.user.userID) || this.userId || "—")], ...rows];
    },
  },
  created() {
    this.loadHeader();
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
    // Lock background scroll while the modal is open.
    this._prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = this._prevOverflow || "";
  },
  methods: {
    // Shared with the backend's slug rule — see utils/postUrl.js.
    postUrl,
    actionLabel,
    clickDetail,
    catLabel: categoryLabel,
    catBadgeClass: categoryBadgeClass,
    // A user can hit the site from several IPs in a day; show the first (public
    // preferred, ordered by the backend) and hang the rest off a +N badge.
    extraIpCount(row) {
      return Math.max(0, ((row && row.ips && row.ips.length) || 0) - 1);
    },
    ipTitle(row) {
      return row && row.ips && row.ips.length ? row.ips.join(", ") : "";
    },
    onKeydown(e) {
      if (e.key === "Escape") this.close();
    },
    close() {
      this.$emit("close");
    },
    async loadHeader() {
      if (!this.userId && !this.email && !this.userName) {
        this.loadError = "No user selected.";
        return;
      }
      this.headerLoading = true;
      this.loadError = "";
      try {
        const detailRes = await axiosInst.post("/api/userstats/user-detail", {
          userID: this.userId || undefined,
          email: this.email || undefined,
          userName: this.userName || undefined,
        });
        this.user = detailRes.data.user;
        this.details = detailRes.data.details;
        // Load the activity summary only once we know the resolved userID.
        if (this.uid) {
          const summaryRes = await axiosInst.post("/api/userstats/user-activity", {
            userID: this.uid,
            section: "summary",
          });
          this.summary = summaryRes.data;
        }
      } catch (err) {
        this.loadError =
          (err.response && err.response.data && (err.response.data.error || err.response.data.message)) ||
          "Failed to load user";
      } finally {
        this.headerLoading = false;
      }
    },
    selectTab(key) {
      if (this.tab === key) return;
      this.tab = key;
      this.page = 1;
      this.rows = [];
      if (key !== "overview") this.fetchTab();
    },
    async fetchTab() {
      if (!this.uid) return;
      this.tabLoading = true;
      try {
        const { data } = await axiosInst.post("/api/userstats/user-activity", {
          userID: this.uid,
          section: this.tab,
          page: this.page,
          limit: 25,
        });
        this.rows = data.items || [];
        this.totalPages = data.totalPages || 0;
        this.totalCount = data.totalCount || 0;
      } catch (err) {
        this.loadError =
          (err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
          "Failed to load activity";
      } finally {
        this.tabLoading = false;
      }
    },
    goPage(p) {
      this.page = p;
      this.fetchTab();
    },
    rowNum(i) {
      return (this.page - 1) * 25 + i + 1;
    },
    toRows(obj, extraHidden) {
      if (!obj) return [];
      return Object.entries(obj)
        .filter(
          ([k, v]) =>
            !HIDDEN_KEYS.has(k) &&
            !(extraHidden && extraHidden.has(k)) &&
            v !== null && v !== "" && typeof v !== "object"
        )
        .map(([k, v]) => [this.labelize(k), String(v)]);
    },
    labelize(k) {
      return k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
    },
    isActive(row) {
      return row.isActive === true || row.isActive === "true";
    },
    cleanTitle(t) {
      return (t || "").replace(/\s+/g, " ").trim();
    },
    fmtDate(v) {
      if (!v) return "—";
      const d = new Date(v);
      if (isNaN(d)) return v;
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    },
    fmtDateTime(v) {
      if (!v) return "—";
      const d = new Date(v);
      if (isNaN(d)) return v;
      return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.uam-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.55);
  z-index: 2147483000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
}
.uam-modal {
  background: var(--g-surface, #fff);
  color: var(--g-text, #111827);
  width: 100%;
  max-width: 1120px;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 80px);
}
/* The tab tables carry more columns than they used to (IP, Clicks, Login
   Time). `.admin-table-wrap` is `overflow: hidden` in admin-theme.css — right
   for a full-page report, where the page itself is the scroller, but inside a
   width-capped modal it silently CLIPS the rightmost columns with no way to
   reach them. The modal is its own viewport, so it has to scroll its own
   tables. */
.uam-body .admin-table-wrap {
  overflow-x: auto;
}
/* Below this the columns stop fitting and the wrap above starts scrolling. */
.uam-body .admin-table-wrap .admin-table {
  min-width: 720px;
}
.uam-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--g-border, #e5e7eb);
  background: var(--g-surface, #fff);
}
.uam-head__titles { min-width: 0; }
.uam-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--g-text, #111827);
}
.uam-sub {
  margin: 6px 0 0;
  font-size: 13.5px;
  color: var(--g-text-secondary, #5b6472);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.uam-close {
  flex: none;
  width: 34px;
  height: 38px;
  border: 1px solid var(--g-border, #e5e7eb);
  border-radius: var(--g-radius-sm);
  background: var(--g-surface, #fff);
  color: var(--g-text-secondary, #5b6472);
  cursor: pointer;
  font-size: 15.5px;
  line-height: 1;
  transition: background 0.12s, color 0.12s;
}
.uam-close:hover { background: var(--g-hover, #f3f4f6); color: var(--g-text, #111827); }
.uam-body {
  padding: 18px 22px 24px;
  overflow-y: auto;
}
.uam-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  padding: 16px;
}
.uam-kv .admin-card__title { margin-bottom: 10px; }
.uam-key {
  font-weight: 600;
  color: var(--g-text-secondary, #5b6472);
  white-space: nowrap;
  width: 40%;
}
.uam-ip-more {
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 4px;
  background: var(--g-hover, #f6f7fb);
  color: var(--g-text-secondary, #667085);
  font-size: 11px;
  font-weight: 600;
}
.uam-nowrap { white-space: nowrap; }
/* The session stamp is context for the click's own time, not the headline. */
.uam-muted { color: var(--g-text-secondary, #667085); }
.uam-msg { max-width: 420px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
